/**
 * Org Detail / Users tab.
 *
 * Consumes the 32b proxy endpoint GET /api/platform/orgs/:id/users.
 * Same 503 SERVER_UNAVAILABLE handling pattern as the Projects tab.
 */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingSkeleton } from '@repro/ui';
import { api } from '../../../utils/api';
import type { ServerUser } from '../../../types/platform';
import type { OrgDetailContext } from '../OrgDetail';

export default function Users() {
  const { organisation } = useOutletContext<OrgDetailContext>();
  const [users, setUsers] = useState<ServerUser[] | null>(null);
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setServerUnavailable(false);
    api
      .getPlatformOrgUsers(organisation.id)
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setServerUnavailable(true);
          setUsers([]);
        } else {
          setUsers(data);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load users');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisation.id]);

  if (loading) {
    return (
      <div className="mt-4">
        <LoadingSkeleton rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <ErrorState title="Couldn't load users" body={error} />
      </div>
    );
  }

  if (serverUnavailable) {
    return (
      <div className="mt-4">
        <EmptyState
          title="Production server unavailable"
          body="The upstream RePro server didn't respond. Try again in a moment."
        />
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="mt-4">
        <EmptyState title="No users" body="This organisation has no users yet." />
      </div>
    );
  }

  return (
    <div className="mt-4 bg-base-100 shadow rounded-lg overflow-hidden">
      <table className="table w-full">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <div className="font-medium text-base-content">{formatName(user)}</div>
                {user.flag_site_admin && <span className="text-xs text-warning">Site admin</span>}
              </td>
              <td className="text-sm text-base-content/80">{user.email}</td>
              <td className="text-sm">{user.role}</td>
              <td>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(
                    user.status?.name
                  )}`}
                >
                  {user.status?.name ?? '—'}
                </span>
              </td>
              <td className="text-sm text-base-content/70">
                {user.last_seen_at ? formatDate(user.last_seen_at) : 'Never'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatName(user: ServerUser): string {
  const first = user.name?.trim() ?? '';
  const last = user.last_name?.trim() ?? '';
  const full = [first, last].filter(Boolean).join(' ');
  return full || user.email;
}

function statusClass(status: string | undefined): string {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'pending':
    case 'invited':
      return 'bg-yellow-100 text-yellow-800';
    case 'disabled':
    case 'suspended':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-base-200 text-base-content/70';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
